import { PROFILEL_URL } from "api/routes";
import Button from "components/_button/form/submit";
import useWidth from "components/_getWidth";
import ActivityTypes from "components/contractor/information/activity-types";
import WhiteBox from "components/contractor/information/details";
import Info from "components/contractor/information/info";
import CustomerRating from "components/contractor/information/info/customer-rating";
import { ContractorPageConfig } from "config/routeConfig";
import { useAppContext } from "context/state";
import useCustomRouter from "hooks/useCustomRouter";
import { useUpdateProject } from "hooks/useRequest/project/projectRequest";
import dynamic from "next/dynamic";
import Head from "next/head";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import abTestService from "services/ab-test.service";
import LastProjectService from "services/last-project";
import selectedContractorService from "services/selected-contractor.service";
import styles from "./id.module.scss";

const SelectContractorAction = dynamic(() => import("components/contractor/select-actions"))
const WorkSamples = dynamic(() => import("components/contractor/wrok-samples/index"));
const ModelVideos = dynamic(() => import("components/contractor/model-videos/index"));
const Comments = dynamic(() => import("components/comments"));



export default function Contractor({ profile, id }) {
  /////////////////////////////////// hooks and consts //////////////////////////////////////

  const { push } = useCustomRouter()

  const allWorkSamplesRef = useRef();

  const [sticky, setSticky] = useState(false);
  const [visibleActions, setVisibleActions] = useState(false);

  const { allWrokSampleStatus } = useAppContext();
  const { width } = useWidth();
  const contractorName =
    (profile?.data?.user?.name ?? "") +
    " " +
    (profile?.data?.user?.lastname ?? "نامشخص");

  const { mutateAsync: updateProject, isLoading: updateProjectIsLoading, isError: updateProjectIsError, error: updateProjectError } = useUpdateProject()

  /////////////////////////////////// functions //////////////////////////////////////

  // this func makes the button sticky on scroll(only tablet and mobile sizes)
  const handleScroll = () => {
    if (width < 1024 && window.scrollY >= 395) {
      setSticky(true);
    } else {
      setSticky(false);
    }
  };

  const orderWithThisContractor = useCallback(async () => {
    let newContractor = null


    if (LastProjectService.getId()) {
      await updateProject({
        id: LastProjectService.getId(),
        data: {
          selectedContractor: profile.data._id,
          ...(abTestService.includesInstallmentUser() && abTestService.isLookingForInstallments() && {
            looksForInstallments: true
          })
        }
      }).then(() => {
        newContractor = {
          _id: profile.data?._id,
          user: profile.data?.user,
          level: profile.data?.level,
          totalCommentsStar: profile.data?.totalCommentsStar,
          totalCommentsCount: profile.data?.totalCommentsCount
        }

        setVisibleActions(true)
      })

    } else if (selectedContractorService.getContractors()?.length < 2) {
      newContractor = {
        _id: profile.data?._id,
        user: profile.data?.user,
        level: profile.data?.level,
        totalCommentsStar: profile.data?.totalCommentsStar,
        totalCommentsCount: profile.data?.totalCommentsCount
      }
      selectedContractorService.setNewContractor(newContractor)

      return push("/product?form_type=contractor", { preserveQuery: ["utm_source", "utm_campaign"] })
    } else {
      return push("/product?form_type=contractor", { preserveQuery: ["utm_source", "utm_campaign"] })
    }

    selectedContractorService.setNewContractor(newContractor)
    localStorage.setItem("selectedContractorBefore", "true")

  }, [profile])

  /////////////////////////////////// useEffects //////////////////////////////////////

  useEffect(() => {
    ContractorPageConfig.title = `دکوراتور ${contractorName} `;
    document.title = `دکوراتور ${contractorName} `;
  }, []);

  useEffect(() => {
    if (width < 1024) {
      window.addEventListener("scroll", handleScroll, { passive: true });
    }
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);


  useEffect(() => {
    if (updateProjectIsError) {
      toast.error(updateProjectError.response?.data?.message)
    }
  }, [updateProjectIsError])




  /////////////////////////////////// jsx //////////////////////////////////////
  return (
    <>
      <Head>
        <link rel="canonical" href={"https://salamsakhteman.com/contractors/" + id} key="canonical" />
      </Head>


      <div className={styles.specialist_container}>
        <div className={styles.container__first_section}>
          <ul className={styles.blobs__container}>
            <li></li>
            <li></li>
            <li></li>
            <li></li>
          </ul>

          <Info data={profile} />

          <ActivityTypes
            slidesPerView={3.5}
            spaceBetween={10}
            data={profile?.data.serviceType}
            centeredSlides
          />

          <div className={styles.action__first_section}>
            <div className={styles.wrapper__action + ` ${sticky ? styles.sticky : ""}`}>
              <div className={styles.contract_label__wrapper + " flex-column gap-5"}>
                <CustomerRating doneProjectsCount={profile.data.doneProjectsCount} totalCommentsCount={profile.data.totalCommentsCount} />
              </div>

              <Button
                type="submit"
                id="connect_btn"
                active
                className={
                  styles.connect_btn__wrapper +
                  ` primary__btn flex align-i-center gap-10`
                }
                onClick={() => orderWithThisContractor()}
                disabled={updateProjectIsLoading}
                loading={updateProjectIsLoading}
                animation
              >
                <span>+</span> انتخاب این متخصص
              </Button>
            </div>

            <p className={styles.caption__action + " mobile_only"}>
              <span className={styles.icon__caption}>!</span>
              {
                profile.data?.isDesigner ?
                  <>
                    با زدن دکمه بالا <span className="fw-900">شماره تلفن طراح</span> برایتان ارسال می‌شود.
                  </>
                  :
                  <>
                    با زدن دکمه بالا، متخصص جهت<span className="fw-900">قیمت‌دهی و بازدید</span>تماس خواهد گرفت.
                  </>
              }
            </p>
          </div>
        </div>

        <div
          ref={allWorkSamplesRef}
          style={allWrokSampleStatus ? { padding: 0 } : {}}
          className={styles.container__second_section}
        >
          {!allWrokSampleStatus && <WhiteBox data={profile} />}

          <WorkSamples
            allWorkSamplesRef={allWorkSamplesRef}
            contractor={profile.data}
          />

          {profile.data?.videos?.length > 0 && (
            <ModelVideos videos={profile.data.videos} />
          )}

          {!allWrokSampleStatus && (
            <section className="pb-xl mt-2xl" id="comments">
              <Comments
                params={{ contractorId: profile.data?._id }}
                contractorId={profile.data?._id}
                haveHeader
              />
            </section>
          )}
        </div>
      </div>

      <SelectContractorAction
        visible={visibleActions}
        contractorUser={profile.data.user}
      />
    </>
  );
}

export async function getServerSideProps(params) {
  params.res.setHeader(
    "Cache-Control",
    "public, s-maxage=3600, stale-while-revalidate=7200"
  );

  const jsonData = await fetch(
    process.env.NEXT_PUBLIC_BASE_URL +
    PROFILEL_URL +
    params.query.id +
    "/profiles"
  );
  const data = await jsonData.json();

  if (!data.successful) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      profile: data,
      id: params.query.id
    },
  };
}

Contractor.config = ContractorPageConfig;
